import styled from 'styled-components';
import Tooltip from './Tooltip';

type IconButtonProps = {
  children: JSX.Element;
  disabled?: boolean;
  options: {
    backgroundColor?: string;
    color?: string;
    hoverColor?: string;
    size: 'xlarge' | 'large' | 'medium' | 'small';
  };
  tooltipText?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

type ButtonProps = {
  backgroundColor?: string;
  hoverColor?: string;
  size: {
    height: string;
    width: string;
  };
};

const IconButton = ({
  children,
  options = { size: 'medium' },
  tooltipText,
  disabled = false,
  onClick,
}: IconButtonProps) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick && onClick(event);
  };

  const sizes = {
    small: {
      height: '1.2rem',
      width: '1.2rem',
    },
    medium: {
      height: '1.5rem',
      width: '1.5rem',
    },
    large: {
      height: '1.8rem',
      width: '1.8rem',
    },
    xlarge: {
      height: '2.2rem',
      width: '2.2rem',
    },
  };

  return tooltipText ? (
    <Tooltip text={tooltipText}>
      <Button
        {...options}
        size={sizes[options.size]}
        disabled={disabled}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Tooltip>
  ) : (
    <Button
      {...options}
      size={sizes[options.size]}
      disabled={disabled}
      onClick={handleClick}
    >
      {children}
    </Button>
  );
};

export default IconButton;

const Button = styled.button<ButtonProps>`
  height: ${({ size }) => size.height};
  width: ${({ size }) => size.width};
  padding: 0;
  border: 0;
  outline: 0;
  background-color: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : 'transparent'};
  border-radius: ${({ backgroundColor }) => (backgroundColor ? '50%' : '0')};
  cursor: pointer;
  position: relative;
  color: ${({ color }) => (color ? color : 'inherit')};

  &:enabled {
    &:before {
      content: '';
      position: absolute;
      display: block;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: #202124;
      border: 0.0625rem solid #000;
      border-radius: 50%;
      transform: scale(0);
      opacity: 0;
      transition: all 0.15s;
    }

    &:hover {
      color: ${({ hoverColor }) => (hoverColor ? hoverColor : '#fc8e49')};

      &:before {
        transform: scale(1);
        opacity: 0.06;
      }
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
